'use client'

import { motion } from 'motion/react'
import { useLocale, useTranslations } from 'next-intl'

import { ModelsNews } from '@/api/generated-api'
import Button from '@/shared/ui/Button'
import Loader from '@/shared/ui/Loader'

import {
  fadeInUp,
  revealViewport,
  staggerContainer
} from '../../utils/animations'
import type { NewsTagFilter } from '../../utils/newsTags'
import NewsGridCard from '../NewsCard/NewsGridCard'
import NewsCategoryNav from '../NewsCategoryNav'
import S from './NewsList.module.scss'

interface NewsListViewProps {
  items: ModelsNews[]
  selectedTag: NewsTagFilter
  onTagChange: (tag: NewsTagFilter) => void
  isLoading?: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}

const NewsListView = ({
  items,
  selectedTag,
  onTagChange,
  isLoading = false,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}: NewsListViewProps) => {
  const t = useTranslations('news')
  const locale = useLocale()

  return (
    <motion.section
      className={S.newsList}
      aria-busy={isLoading}
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={revealViewport}
    >
      <NewsCategoryNav selectedTag={selectedTag} onTagChange={onTagChange} />
      <motion.div className={S.newsGrid} variants={staggerContainer}>
        {isLoading ? (
          <div className={S.loading}>
            <Loader variant="inline" />
          </div>
        ) : items.length > 0 ? (
          items.map((news, index) => (
            <motion.article
              key={news.id}
              variants={fadeInUp}
              custom={index % 6}
              initial="hidden"
              whileInView="visible"
              viewport={revealViewport}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <NewsGridCard news={news} locale={locale} />
            </motion.article>
          ))
        ) : (
          <div className={S.emptyState}>{t('list.empty')}</div>
        )}
      </motion.div>

      {hasNextPage && (
        <div className={S.loadMoreWrapper}>
          <Button
            color="secondary"
            size="m"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className={S.loadMoreButton}
          >
            {isFetchingNextPage ? (
              <>
                <Loader variant="small" />
                {t('list.loading')}
              </>
            ) : (
              t('list.loadMore')
            )}
          </Button>
        </div>
      )}
    </motion.section>
  )
}

export default NewsListView
